import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEBOUNCE_VARIANT_DARK } from '@components/debounced-input-search/src/debounced-input-search.type';
import { Modal, SearchItemModal } from '@components/modals';
import { EMAIL_REGEX } from '@constants/global';
import { AuthContext } from '@contexts/auth/auth-context';
import { GetAdminUsersResponse } from '@hooks/use-get-admin-users/use-get-admin-users';

import { ImpersonateUserListContent } from './impersonate-user-list-content';
import { ImpersonateUserLogOut } from './impersonate-user-logout';
import {
  IMPERSONATE_STEP_LIST,
  IMPERSONATE_STEP_LOGOUT,
  IMPERSONATE_STEP_SELECT,
  ImpersonateStep,
  ImpersonateUserProps,
} from './impersonate-user-modal.type';
import { ImpersonateUserSelectContent } from './impersonate-user-select-content';

export const ImpersonateUserModal = ({ isImpersonateModalOpen, setIsImpersonateModalOpen }: ImpersonateUserProps) => {
  const { t } = useTranslation();
  const { changeUser, isImpersonated } = useContext(AuthContext);

  const [step, setStep] = useState<ImpersonateStep>(isImpersonated ? IMPERSONATE_STEP_LOGOUT : IMPERSONATE_STEP_SELECT);
  const [emailToSearch, setEmailToSearch] = useState<string | undefined>();
  const [selectedUser, setSelectedUser] = useState<GetAdminUsersResponse | undefined>();

  const handleClickOutsideImpersonateModal = () => {
    setStep(isImpersonated ? 'logout' : 'select');
    setIsImpersonateModalOpen(false);
    setSelectedUser(undefined);
  };

  const handleSearch = (value: string) => {
    const validateEmail = value.toLowerCase().match(EMAIL_REGEX);

    if (validateEmail) setEmailToSearch(value);
  };

  const handleClickSaveUser = () => {
    if (selectedUser) changeUser({ email: selectedUser.email, uuid: selectedUser.uuid });

    handleClickOutsideImpersonateModal();
  };

  return (
    <>
      {step === IMPERSONATE_STEP_SELECT && (
        <Modal isOpen={isImpersonateModalOpen} handleClose={handleClickOutsideImpersonateModal} variant='md'>
          <ImpersonateUserSelectContent setStep={setStep} handleClose={handleClickOutsideImpersonateModal} />
        </Modal>
      )}

      {step === IMPERSONATE_STEP_LIST && (
        <SearchItemModal
          handleClose={handleClickOutsideImpersonateModal}
          isOpen={isImpersonateModalOpen}
          placeholder={t('common:emailFormFieldLabel')}
          title={t('common:impersonateModalTitle')}
          buttonPrimaryText={t('common:doneButton')}
          buttonSecondaryText={t('common:discard')}
          handlePrimaryButton={handleClickSaveUser}
          isButtonPrimaryDisabled={!selectedUser}
          handleSecondaryButton={handleClickOutsideImpersonateModal}
          handleSearch={handleSearch}
          showCloseIconButton={true}
          debounceInputVariant={DEBOUNCE_VARIANT_DARK}>
          <ImpersonateUserListContent email={emailToSearch} setSelectedUser={setSelectedUser} />
        </SearchItemModal>
      )}

      {step === IMPERSONATE_STEP_LOGOUT && (
        <Modal isOpen={isImpersonateModalOpen} handleClose={handleClickOutsideImpersonateModal} variant='md'>
          <ImpersonateUserLogOut />
        </Modal>
      )}
    </>
  );
};
